/**
 * GLSL ES 3.00 ports of the SkSL in docs/spec/03-rendering.md §4 (kept line-for-line where
 * possible). Conventions: every offscreen target stores image rows top-first, so in offscreen
 * passes `gl_FragCoord.xy` IS the Skia local coordinate `xy` (pixel centers at .5) and
 * `texture(tex, xy / size)` IS `shader.eval(xy)` for an image scaled to W×H.
 * Textures are plain RGBA8 / R8 / RGBA16F (never sRGB formats): colors are raw sRGB-encoded
 * numbers and are linearized manually (`toLinear`), like the original.
 */

const HEADER = `#version 300 es
precision highp float;
precision highp int;
`;

/** §4.1 COMMON. */
const COMMON = `
float luma(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }

vec3 toLinear(vec3 c) {
  return mix(c / 12.92, pow((c + 0.055) / 1.055, vec3(2.4)), step(vec3(0.04045), c));
}

float hash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float skyLikelihood(vec3 srgb, vec2 uv, float overcastWeight) {
  float maxc = max(srgb.r, max(srgb.g, srgb.b));
  float minc = min(srgb.r, min(srgb.g, srgb.b));
  float sat = (maxc - minc) / max(maxc, 0.001);
  float l = luma(srgb);
  float blueSky = smoothstep(0.02, 0.12, srgb.b - srgb.r) * smoothstep(0.42, 0.7, l);
  float overcast = smoothstep(0.86, 0.94, l) * (1.0 - smoothstep(0.06, 0.16, sat)) * overcastWeight;
  float green = smoothstep(0.0, 0.06, srgb.g - srgb.b);
  float upper = 1.0 - smoothstep(0.35, 0.8, uv.y);
  return clamp(max(blueSky, overcast) * upper * (1.0 - green), 0.0, 1.0);
}

float depthSky(float d, vec3 srgb) {
  return (1.0 - smoothstep(0.04, 0.12, d)) * smoothstep(0.38, 0.62, luma(srgb));
}
`;

/* ------------------------------------------------------------------ vertex shaders */

/** Full-screen triangle (no attributes). */
export const FULLSCREEN_VS = `${HEADER}
void main() {
  vec2 p = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}`;

/** Image px → clip for offscreen targets (rows top-first, so no y flip). */
const TO_CLIP = `
uniform vec2 u_size;
vec4 toClip(vec2 px) { return vec4(px / u_size * 2.0 - 1.0, 0.0, 1.0); }
vec2 corner() { return vec2(float(gl_VertexID & 1), float((gl_VertexID >> 1) & 1)); }
`;

/** WASH instances: [x, y, r, g, b]; quad from uniforms (LED-local coords). */
export const WASH_VS = `${HEADER}${TO_CLIP}
in vec2 a_center;
in vec3 a_color;
uniform vec4 u_quad;
out vec2 v_p;
flat out vec3 v_color;
void main() {
  vec2 local = u_quad.xy + corner() * u_quad.zw;
  v_p = local;
  v_color = a_color;
  gl_Position = toClip(a_center + local);
}`;

/** EMITTER instances: center, color, (rc, core, halo, bloom), (bloomR, extent). */
export const EMITTER_VS = `${HEADER}${TO_CLIP}
in vec2 a_center;
in vec3 a_color;
in vec4 a_p0;
in vec2 a_p1;
out vec2 v_p;
flat out vec3 v_color;
flat out vec4 v_p0;
flat out vec2 v_p1;
void main() {
  float ext = a_p1.y;
  vec2 local = (corner() * 2.0 - 1.0) * ext;
  v_p = local;
  v_color = a_color;
  v_p0 = a_p0;
  v_p1 = a_p1;
  gl_Position = toClip(a_center + local);
}`;

/** Stroke segments for the coverage mask: a, b, (buttStart, buttEnd). */
export const SEGMENT_VS = `${HEADER}${TO_CLIP}
in vec2 a_a;
in vec2 a_b;
in vec2 a_flags;
uniform float u_hw;
out vec2 v_uv;
flat out float v_len;
flat out vec2 v_flags;
void main() {
  vec2 d = a_b - a_a;
  float len = length(d);
  vec2 dir = len > 1e-6 ? d / len : vec2(1.0, 0.0);
  vec2 n = vec2(-dir.y, dir.x);
  vec2 c = corner();
  float pad = u_hw + 1.5;
  float along = mix(-pad, len + pad, c.x);
  float across = mix(-pad, pad, c.y);
  v_uv = vec2(along, across);
  v_len = len;
  v_flags = a_flags;
  gl_Position = toClip(a_a + dir * along + n * across);
}`;

/** Antialiased circles: center per instance, radius + color uniforms. */
export const CIRCLE_VS = `${HEADER}${TO_CLIP}
in vec2 a_center;
uniform float u_radius;
out vec2 v_p;
void main() {
  vec2 local = (corner() * 2.0 - 1.0) * (u_radius + 1.5);
  v_p = local;
  gl_Position = toClip(a_center + local);
}`;

/** LDR conical wash instances: [x, y, r, g, b]. */
export const LDR_WASH_VS = WASH_VS;

/** LDR emitter instances: center, kind, radius, glow, c, m72, m25. */
export const LDR_EMITTER_VS = `${HEADER}${TO_CLIP}
in vec2 a_center;
in vec3 a_kind;
in vec3 a_c;
in vec3 a_m72;
in vec3 a_m25;
out vec2 v_p;
flat out vec3 v_kind;
flat out vec3 v_c;
flat out vec3 v_m72;
flat out vec3 v_m25;
void main() {
  float radius = a_kind.y;
  vec2 local = (corner() * 2.0 - 1.0) * (radius + 1.5);
  v_p = local;
  v_kind = a_kind;
  v_c = a_c;
  v_m72 = a_m72;
  v_m25 = a_m25;
  gl_Position = toClip(a_center + local);
}`;

/** Screen presentation: a textured rect in device px on the default framebuffer (y up). */
export const PRESENT_VS = `${HEADER}
uniform vec2 u_view;
uniform vec4 u_rect;
uniform vec4 u_uv;
out vec2 v_uv;
void main() {
  vec2 c = vec2(float(gl_VertexID & 1), float((gl_VertexID >> 1) & 1));
  vec2 px = u_rect.xy + c * u_rect.zw;
  v_uv = u_uv.xy + c * u_uv.zw;
  gl_Position = vec4(px.x / u_view.x * 2.0 - 1.0, 1.0 - px.y / u_view.y * 2.0, 0.0, 1.0);
}`;

/* ---------------------------------------------------------------- fragment shaders */

/** §4.2 GRADE_HDR. */
export const GRADE_HDR_FS = `${HEADER}
uniform sampler2D u_photo;
uniform sampler2D u_depth;
uniform vec2 u_size;
uniform float u_exposure;
uniform float u_skyExposure;
uniform float u_desat;
uniform float u_blue;
uniform float u_dusk;
uniform float u_skyAmount;
uniform float u_hasDepth;
uniform float u_scale;
out vec4 o;
${COMMON}
void main() {
  vec2 xy = gl_FragCoord.xy;
  vec3 srgb = texture(u_photo, xy / u_size).rgb;
  vec3 lin = toLinear(srgb);
  vec2 uv = xy / u_size;

  float sky = skyLikelihood(srgb, uv, 0.6);
  if (u_hasDepth > 0.5) sky = max(sky * 0.35, depthSky(texture(u_depth, xy / u_size).r, srgb));
  sky *= u_skyAmount;

  float lumLin = luma(lin);
  vec3 ground = mix(lin, vec3(lumLin), u_desat);
  ground *= mix(vec3(1.0), vec3(0.56, 0.72, 1.0), u_blue);
  ground *= u_exposure;
  ground += vec3(0.0005, 0.0008, 0.0016) * u_blue;

  vec3 zenith = mix(vec3(0.10, 0.20, 0.55), vec3(0.010, 0.020, 0.058), u_blue);
  vec3 horizon = mix(vec3(0.40, 0.42, 0.62), vec3(0.032, 0.042, 0.078), u_blue);
  horizon = mix(horizon, vec3(1.0, 0.45, 0.22), u_dusk * 0.85);
  vec3 skyCol = mix(zenith, horizon, smoothstep(0.0, 0.75, uv.y));
  skyCol *= (0.55 + 0.9 * lumLin) * u_skyExposure;

  o = vec4(mix(ground, skyCol, sky) * u_scale, 1.0);
}`;

/** §4.3 ALBEDO_HDR. */
export const ALBEDO_HDR_FS = `${HEADER}
uniform sampler2D u_photo;
uniform sampler2D u_depth;
uniform vec2 u_size;
uniform float u_hasDepth;
uniform float u_runDepth;
uniform float u_depthSigma;
out vec4 o;
${COMMON}
vec3 photoAt(vec2 xy) { return texture(u_photo, xy / u_size).rgb; }
float foliage(vec2 xy, vec3 c) {
  float maxc = max(c.r, max(c.g, c.b));
  float sat = (maxc - min(c.r, min(c.g, c.b))) / max(maxc, 0.001);
  float leafHue = smoothstep(0.03, 0.10, c.g - c.b) * step(c.r, c.g * 1.12);
  float off = max(1.5, u_size.x / 640.0);
  float l0 = luma(c);
  float contrast = abs(luma(photoAt(xy + vec2(off, 0.0))) - l0)
                 + abs(luma(photoAt(xy + vec2(0.0, off))) - l0)
                 + abs(luma(photoAt(xy - vec2(off, off))) - l0);
  return leafHue * smoothstep(0.22, 0.38, sat) * smoothstep(0.2, 0.38, contrast);
}
void main() {
  vec2 xy = gl_FragCoord.xy;
  vec3 srgb = photoAt(xy);
  vec2 uv = xy / u_size;
  vec3 lin = toLinear(srgb);
  vec3 albedo = clamp(pow(lin, vec3(0.8)) * 1.25, 0.0, 1.0);
  float keep = 1.0 - skyLikelihood(srgb, uv, 0.9);
  keep *= 1.0 - 0.85 * foliage(xy, srgb);
  if (u_hasDepth > 0.5) {
    float d = texture(u_depth, xy / u_size).r;
    float dd = (d - u_runDepth) / u_depthSigma;
    keep *= (1.0 - depthSky(d, srgb)) * exp(-dd * dd);
  }
  o = vec4(albedo * keep, 1.0);
}`;

/** §4.4 WASH. */
export const WASH_FS = `${HEADER}
uniform float u_d0;
uniform float u_reach;
in vec2 v_p;
flat in vec3 v_color;
out vec4 o;
void main() {
  vec2 p = v_p;
  float y = p.y;
  float r2 = p.x * p.x + y * y + u_d0 * u_d0;
  float e = max(y, 0.0) * u_d0 / (r2 * r2) * u_d0 * u_d0 * 3.0792;
  e += 0.12 * exp(-(p.x * p.x + y * y) / (0.5 * u_d0 * u_d0));
  e *= 1.0 - smoothstep(u_reach * 0.55, u_reach, length(p));
  o = vec4(v_color * e, min(e, 1.0));
}`;

/** §4.5 EMITTER. */
export const EMITTER_FS = `${HEADER}
in vec2 v_p;
flat in vec3 v_color;
flat in vec4 v_p0;
flat in vec2 v_p1;
out vec4 o;
void main() {
  float rc = v_p0.x, core = v_p0.y, halo = v_p0.z, bloom = v_p0.w;
  float bloomR = v_p1.x, extent = v_p1.y;
  float r = length(v_p);
  float c = 1.0 - smoothstep(rc * 0.45, rc, r);
  float h = 1.0 / (1.0 + (r * r) / (rc * rc));
  h = h * h;
  float b = bloomR > 0.0 ? exp(-(r * r) / (bloomR * bloomR)) : 0.0;
  float e = (core * c + halo * h + bloom * b) * (1.0 - smoothstep(extent * 0.6, extent, r));
  o = vec4(v_color * e, min(e, 1.0));
}`;

/** Stroke coverage (union of capsules via MAX blending; optional butt ends). */
export const SEGMENT_FS = `${HEADER}
uniform float u_hw;
in vec2 v_uv;
flat in float v_len;
flat in vec2 v_flags;
out vec4 o;
void main() {
  float u = v_uv.x, v = v_uv.y;
  float d;
  if (u < 0.0 && v_flags.x > 0.5) d = max(abs(v) - u_hw, -u);
  else if (u > v_len && v_flags.y > 0.5) d = max(abs(v) - u_hw, u - v_len);
  else d = length(vec2(u - clamp(u, 0.0, v_len), v)) - u_hw;
  o = vec4(clamp(0.5 - d, 0.0, 1.0));
}`;

/** Composite a coverage mask with a premultiplied color (SrcOver). */
export const COVERAGE_FS = `${HEADER}
uniform sampler2D u_mask;
uniform vec4 u_color;
out vec4 o;
void main() {
  float cov = texelFetch(u_mask, ivec2(gl_FragCoord.xy), 0).r;
  o = u_color * cov;
}`;

/** Antialiased filled circle, premultiplied color. */
export const CIRCLE_FS = `${HEADER}
uniform float u_radius;
uniform vec4 u_color;
in vec2 v_p;
out vec4 o;
void main() {
  float cov = clamp(u_radius + 0.5 - length(v_p), 0.0, 1.0);
  o = u_color * cov;
}`;

/** §4.6 SOLID (full frame). */
export const SOLID_FS = `${HEADER}
uniform vec4 u_color;
out vec4 o;
void main() { o = u_color; }`;

/** §4.7 VIGNETTE (drawn with Modulate). */
export const VIGNETTE_FS = `${HEADER}
uniform vec2 u_size;
uniform float u_amount;
out vec4 o;
void main() {
  vec2 d = gl_FragCoord.xy / u_size - 0.5;
  float v = 1.0 - u_amount * dot(d, d) * 1.4;
  o = vec4(v, v, v, 1.0);
}`;

/** Copy a same-size layer (composited with the bound blend mode). */
export const COPY_FS = `${HEADER}
uniform sampler2D u_tex;
out vec4 o;
void main() { o = texelFetch(u_tex, ivec2(gl_FragCoord.xy), 0); }`;

/**
 * §5.2 restore + §4.8 grain: out = sRGB_OETF(clamp(16 v)) into the 8-bit target, then the
 * GRAIN shader blended with Overlay in display space (quantized to 8 bits first, like Skia).
 */
export const RESOLVE_FS = `${HEADER}
uniform sampler2D u_hdr;
uniform float u_grain;
out vec4 o;
float hash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}
void main() {
  vec2 xy = gl_FragCoord.xy;
  vec3 v = texelFetch(u_hdr, ivec2(xy), 0).rgb;
  vec3 x = clamp(v * 16.0, 0.0, 1.0);
  vec3 c = mix(12.92 * x, 1.055 * pow(x, vec3(1.0 / 2.4)) - 0.055, step(vec3(0.0031308), x));
  if (u_grain > 0.0) {
    c = floor(c * 255.0 + 0.5) / 255.0;
    float g = 0.5 + (hash(xy) - 0.5) * u_grain;
    vec3 lo = 2.0 * g * c;
    vec3 hi = 1.0 - 2.0 * (1.0 - g) * (1.0 - c);
    c = mix(lo, hi, step(vec3(0.5), c));
  }
  o = vec4(clamp(c, 0.0, 1.0), 1.0);
}`;

/** §4.9 GRADE_LDR. */
export const GRADE_LDR_FS = `${HEADER}
uniform sampler2D u_photo;
uniform vec2 u_size;
uniform float u_exposure;
uniform float u_skyExposure;
uniform float u_desat;
uniform float u_blue;
uniform float u_dusk;
uniform float u_skyAmount;
uniform float u_grain;
uniform float u_vignette;
out vec4 o;
${COMMON}
void main() {
  vec2 xy = gl_FragCoord.xy;
  vec3 srgb = texture(u_photo, xy / u_size).rgb;
  vec3 lin = srgb * srgb;
  vec2 uv = xy / u_size;
  float sky = skyLikelihood(srgb, uv, 0.6) * u_skyAmount;
  float lumLin = luma(lin);
  vec3 ground = mix(lin, vec3(lumLin), u_desat);
  ground *= mix(vec3(1.0), vec3(0.56, 0.72, 1.0), u_blue);
  ground *= u_exposure;
  ground += vec3(0.0006, 0.0009, 0.0018) * u_blue;
  vec3 zenith = mix(vec3(0.10, 0.20, 0.55), vec3(0.012, 0.022, 0.06), u_blue);
  vec3 horizon = mix(vec3(0.40, 0.42, 0.62), vec3(0.035, 0.045, 0.08), u_blue);
  horizon = mix(horizon, vec3(1.0, 0.45, 0.22), u_dusk * 0.85);
  vec3 skyCol = mix(zenith, horizon, smoothstep(0.0, 0.75, uv.y));
  skyCol *= (0.55 + 0.9 * lumLin) * u_skyExposure;
  vec3 c = mix(ground, skyCol, sky);
  vec2 d = uv - 0.5;
  c *= 1.0 - u_vignette * dot(d, d) * 1.4;
  vec3 outc = sqrt(max(c, vec3(0.0)));
  outc += (hash(xy) - 0.5) * u_grain * 0.035;
  o = vec4(clamp(outc, 0.0, 1.0), 1.0);
}`;

/** §4.10 ALBEDO_LDR. */
export const ALBEDO_LDR_FS = `${HEADER}
uniform sampler2D u_photo;
uniform vec2 u_size;
uniform float u_gain;
out vec4 o;
void main() {
  vec3 c = texture(u_photo, gl_FragCoord.xy / u_size).rgb;
  vec3 lifted = pow(max(c, vec3(0.0)), vec3(0.72));
  float l = dot(lifted, vec3(0.2126, 0.7152, 0.0722));
  o = vec4(clamp(mix(vec3(l), lifted, 1.12) * u_gain, 0.0, 1.0), 1.0);
}`;

/**
 * §6 drawRunWashLdr: two-point conical gradient, start (0, 0.015L) r 0 → end (0, 0.4L) r 0.58L,
 * local matrix scale(0.6, 1), alpha stops perLed × [1, .7, .4, .17, .05, 0] at [0, .08, .24, .48, .74, 1].
 */
export const LDR_WASH_FS = `${HEADER}
uniform float u_L;
uniform float u_perLed;
in vec2 v_p;
flat in vec3 v_color;
out vec4 o;
float stops(float t) {
  t = clamp(t, 0.0, 1.0);
  if (t < 0.08) return mix(1.0, 0.7, t / 0.08);
  if (t < 0.24) return mix(0.7, 0.4, (t - 0.08) / 0.16);
  if (t < 0.48) return mix(0.4, 0.17, (t - 0.24) / 0.24);
  if (t < 0.74) return mix(0.17, 0.05, (t - 0.48) / 0.26);
  return mix(0.05, 0.0, (t - 0.74) / 0.26);
}
void main() {
  vec2 q = vec2(v_p.x / 0.6, v_p.y) - vec2(0.0, u_L * 0.015);
  vec2 d = vec2(0.0, u_L * 0.4 - u_L * 0.015);
  float r1 = u_L * 0.58;
  float a = dot(d, d) - r1 * r1;
  float b = dot(q, d);
  float c = dot(q, q);
  float t = (b - sqrt(max(b * b - a * c, 0.0))) / a;
  float alpha = clamp(u_perLed * stops(t), 0.0, 1.0);
  o = vec4(v_color * alpha, alpha);
}`;

/** §6 drawRunEmittersLdr: radial gradients (bloom, core), unpremultiplied interpolation. */
export const LDR_EMITTER_FS = `${HEADER}
in vec2 v_p;
flat in vec3 v_kind;
flat in vec3 v_c;
flat in vec3 v_m72;
flat in vec3 v_m25;
out vec4 o;
void main() {
  float radius = v_kind.y;
  float glow = v_kind.z;
  float r = length(v_p);
  float t = clamp(r / radius, 0.0, 1.0);
  vec3 col;
  float a;
  if (v_kind.x < 0.5) {
    col = v_c;
    float a0 = min(1.0, 0.55 * glow), a1 = clamp(0.22 * glow, 0.0, 1.0), a2 = clamp(0.08 * glow, 0.0, 1.0), a3 = clamp(0.025 * glow, 0.0, 1.0);
    if (t < 0.08) a = mix(a0, a1, t / 0.08);
    else if (t < 0.25) a = mix(a1, a2, (t - 0.08) / 0.17);
    else if (t < 0.55) a = mix(a2, a3, (t - 0.25) / 0.30);
    else a = mix(a3, 0.0, (t - 0.55) / 0.45);
  } else {
    if (t < 0.3) { float u = t / 0.3; col = mix(vec3(1.0), v_m72, u); a = mix(1.0, 0.98, u); }
    else if (t < 0.52) { float u = (t - 0.3) / 0.22; col = mix(v_m72, v_m25, u); a = mix(0.98, 0.75, u); }
    else if (t < 0.78) { float u = (t - 0.52) / 0.26; col = mix(v_m25, v_c, u); a = mix(0.75, 0.28, u); }
    else { float u = (t - 0.78) / 0.22; col = v_c; a = mix(0.28, 0.0, u); }
  }
  float cov = clamp(radius + 0.5 - r, 0.0, 1.0);
  a *= cov;
  o = vec4(col * a, a);
}`;

/** §7.1 muted photo (color matrix on 8-bit sRGB values). */
export const LINES_PHOTO_FS = `${HEADER}
uniform sampler2D u_photo;
uniform vec2 u_size;
uniform mat3 u_matrix;
out vec4 o;
void main() {
  vec3 c = texture(u_photo, gl_FragCoord.xy / u_size).rgb;
  o = vec4(clamp(u_matrix * c, 0.0, 1.0), 1.0);
}`;

/** On-screen presentation; optional circular clip (loupe) and solid fill mode. */
export const PRESENT_FS = `${HEADER}
uniform sampler2D u_tex;
uniform vec2 u_view;
uniform vec3 u_clip;
uniform vec4 u_solid;
in vec2 v_uv;
out vec4 o;
void main() {
  vec2 sp = vec2(gl_FragCoord.x, u_view.y - gl_FragCoord.y);
  if (u_clip.z > 0.0 && distance(sp, u_clip.xy) > u_clip.z) discard;
  o = u_solid.a > 0.0 ? u_solid : vec4(texture(u_tex, v_uv).rgb, 1.0);
}`;
